import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/layout/Logo";
import { Authenticator, ThemeProvider, useAuthenticator } from "@aws-amplify/ui-react";
import { fetchAuthSession } from "aws-amplify/auth";
import "@aws-amplify/ui-react/styles.css";
import { useRole } from "@/contexts/RoleContext";
import { usePageTitle } from "@/hooks/usePageTitle";

function PostAuthRedirect() {
  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);
  const navigate = useNavigate();

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    fetchAuthSession()
      .then((session) => {
        const token = session.tokens?.accessToken?.toString();
        if (token) sessionStorage.setItem("access_token", token);
        navigate("/auth/callback");
      })
      .catch(() => navigate("/"));
  }, [authStatus, navigate]);

  return null;
}

const amplifyTheme = {
  name: "bmc-theme",
  tokens: {
    colors: {
      brand: {
        primary: {
          10: "hsl(210, 60%, 97%)",
          20: "hsl(210, 60%, 93%)",
          40: "hsl(210, 60%, 75%)",
          60: "hsl(210, 60%, 50%)",
          80: "hsl(210, 60%, 30%)",
          90: "hsl(210, 60%, 22%)",
          100: "hsl(210, 60%, 18%)",
        },
      },
    },
    fonts: {
      default: {
        variable: { value: "Inter, sans-serif" },
        static: { value: "Inter, sans-serif" },
      },
    },
    components: {
      authenticator: {
        router: {
          borderWidth: { value: "0" },
          boxShadow: { value: "none" },
          backgroundColor: { value: "transparent" },
        },
        form: {
          padding: { value: "0" },
        },
      },
      button: {
        primary: {
          backgroundColor: { value: "hsl(210, 60%, 18%)" },
          color: { value: "#ffffff" },
          _hover: {
            backgroundColor: { value: "hsl(210, 60%, 25%)" },
          },
          _active: {
            backgroundColor: { value: "hsl(210, 60%, 15%)" },
          },
          borderRadius: { value: "9999px" },
          fontSize: { value: "0.9375rem" },
          fontWeight: { value: "600" },
        },
        link: {
          color: { value: "hsl(165, 55%, 38%)" },
          _hover: {
            color: { value: "hsl(165, 55%, 30%)" },
            backgroundColor: { value: "transparent" },
          },
        },
      },
      fieldcontrol: {
        borderRadius: { value: "0.625rem" },
        borderColor: { value: "hsl(210, 20%, 86%)" },
        _focus: {
          borderColor: { value: "hsl(210, 60%, 18%)" },
          boxShadow: { value: "0 0 0 2px hsl(210, 60%, 18% / 0.15)" },
        },
        fontSize: { value: "0.9375rem" },
      },
      field: {
        label: {
          color: { value: "hsl(210, 60%, 18%)" },
          fontSize: { value: "0.875rem" },
          fontWeight: { value: "500" },
        },
      },
      tabs: {
        item: {
          color: { value: "hsl(210, 20%, 50%)" },
          _active: {
            color: { value: "hsl(210, 60%, 18%)" },
            borderColor: { value: "hsl(210, 60%, 18%)" },
          },
          _hover: {
            color: { value: "hsl(210, 60%, 30%)" },
          },
          fontSize: { value: "0.9375rem" },
          fontWeight: { value: "600" },
        },
      },
    },
  },
} as const;

export default function LoginPage() {
  usePageTitle('Sign In | BookMyCoaching');
  const { role } = useRole();

  useEffect(() => {
    try { localStorage.setItem("role", role ?? "student"); } catch { /* ignore */ }
  }, [role]);

  return (
    <ThemeProvider theme={amplifyTheme}>
      <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center">
            <div className="w-48 sm:w-56">
              <Logo size="lg" className="w-full" />
            </div>
          </div>

          <div className="mb-8 text-center">
            <h1 className="font-sans text-2xl sm:text-3xl font-bold text-foreground mb-2 leading-snug">
              Welcome back
            </h1>
            <p className="text-muted-foreground text-sm">
              Sign in or create an account to continue
            </p>
          </div>

          <Authenticator
            // socialProviders={["google"]}
            initialState="signIn"
            hideSignUp
            formFields={{
              signIn: {
                username: { label: "Email", placeholder: "you@example.com", isRequired: true },
                password: { label: "Password", placeholder: "Your password", isRequired: true },
              },
            }}
          >
            <PostAuthRedirect />
          </Authenticator>

          <p className="mt-4 text-sm text-center text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/register" className="font-medium text-foreground underline hover:no-underline">
              Sign up
            </Link>
          </p>

          <p className="mt-8 text-xs text-center text-muted-foreground">
            By continuing, you agree to our{" "}
            <Link to="/terms" className="underline hover:text-foreground transition-colors">Terms</Link>
            {" "}and{" "}
            <Link to="/privacy" className="underline hover:text-foreground transition-colors">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </ThemeProvider>
  );
}
